/* commission-portal-live-blueprint-reader.js
   Reads a FramedFantasy "Character Commission Blueprint" PDF (built by buildPDFBlob
   in assets/character-quest.js or assets/character-quest-2.js) back into fields.
   Plain ES5. Exposes:
     window.ffBlueprintFromTextContent(pages)
       pages: array of pdf.js getTextContent() results, in page order.
       returns { ok, reason, variant, blueprint, references, extra }
     window.ffReadBlueprintPdf(file)
       file: File or Blob (an ArrayBuffer or Uint8Array also works).
       returns a Promise of the same object; rejects with a plain message. */
(function () {
  "use strict";

  var PDFJS_URL = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
  var WORKER_URL = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

  // Builder geometry (A4 in pt, 54pt margins): values wrap at pw - 2*margin,
  // reference notes and the footer sentence at 40pt less.
  var VALUE_WRAP = 595.28 - 108;
  var NOTE_WRAP = 595.28 - 148;
  // Every font size the builders draw with, in pt.
  var SIZES = [8, 10, 11, 12, 13, 14, 18, 34];

  var FIELDS = [
    ["genderPronouns", "Gender & Pronouns"], ["personality", "Personality"], ["buildAge", "Build & Age"],
    ["skinDesc", "Skin & Race Features"], ["hairDesc", "Hair"], ["eyeDesc", "Eyes"],
    ["facialFeatures", "Facial Features"], ["distinguishing", "Scars, Tattoos & Markings"],
    ["appendages", "Horns, Tail, Wings, etc."], ["armor", "Armor & Outfit"],
    ["accessories", "Accessories & Jewelry"], ["weapon", "Weapon & Focus"], ["signatureItem", "Signature Item"],
    ["colors", "Color Palette"], ["pets", "Pets & Familiars"], ["vibe", "Portrait Vibe"],
    ["expression", "Expression"], ["pose", "Pose"], ["bgPreference", "Portrait Background"],
    ["definingMoment", "Defining Moment"], ["campaign", "Campaign"], ["mustHave", "Must Capture"],
    ["pleaseAvoid", "Please Avoid"]
  ];
  var TITLES = ["CORE IDENTITY", "PHYSICAL APPEARANCE", "ATTIRE & GEAR", "EXPRESSION & ATMOSPHERE",
    "STORY & BACKSTORY", "STORY", "ARTIST GUIDANCE"];

  var LABEL_KEY = {};
  var SECTION = {};
  var k;
  for (k = 0; k < FIELDS.length; k++) LABEL_KEY[norm(FIELDS[k][1])] = FIELDS[k][0];
  for (k = 0; k < TITLES.length; k++) SECTION[norm(TITLES[k])] = true;

  var SUBTITLE = norm("Character Commission Blueprint");
  var REFERENCES = norm("REFERENCE IMAGES");
  var FOOTER = norm("This blueprint is everything an artist needs to bring");
  var FOOTER_RE = /^This blueprint is everything an artist needs to bring (.+) to life\.$/;
  var CAPTION_RE = /^Reference (\d+) of (\d+)$/;
  var RACE_RE = /^(.*?) \. (.*) \. Level(?: (.*))?$/;
  // pdf.js yields these for bytes it cannot map (e.g. jsPDF's UTF-16 fallback).
  var CONTROL_RE = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g;

  // Upper-case, then keep A-Z and 0-9 only. Character-spaced text such as the
  // section titles comes back from pdf.js as "C O R E I D E N T I T Y".
  function norm(s) {
    return String(s).toUpperCase().replace(/[^A-Z0-9]/g, "");
  }

  function isArray(v) {
    return Object.prototype.toString.call(v) === "[object Array]";
  }

  function has(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }

  // Groups text items into visual lines. pdf.js returns items in content-stream
  // order, which is the order the builder drew them.
  function buildLines(pages) {
    var lines = [];
    var cur = null;
    var p, i;

    function end() {
      if (cur) {
        cur.text = cur.text.replace(/\s+/g, " ").trim();
        if (cur.text) lines.push(cur);
      }
      cur = null;
    }

    for (p = 0; p < pages.length; p++) {
      var items = (pages[p] && pages[p].items) || [];
      for (i = 0; i < items.length; i++) {
        var it = items[i];
        if (!it || typeof it.str !== "string" || !it.transform) continue;
        var t = it.transform;
        var size = Math.sqrt(t[0] * t[0] + t[1] * t[1]); // Math.hypot(t[0], t[1])
        var x = t[4];
        var y = t[5];
        if (cur && Math.abs(y - cur.y) > Math.max(1, cur.size * 0.3)) end();
        var str = it.str.replace(CONTROL_RE, "");
        if (str) {
          var blank = !/\S/.test(str);
          if (!cur) cur = { page: p, y: y, x: x, xEnd: x, size: 0, text: "", started: false };
          if (!blank) {
            if (!cur.started) {
              cur.x = x;
              cur.started = true;
            } else if (x - cur.xEnd > size * 0.15 && !/\s$/.test(cur.text)) {
              cur.text += " ";
            }
            if (size > cur.size) cur.size = size;
          }
          cur.text += str;
          cur.xEnd = x + (it.width || 0);
        }
        if (it.hasEOL) end();
      }
      end();
    }
    return lines;
  }

  function sizeClass(size, scale) {
    var r = size / scale;
    for (var i = 0; i < SIZES.length; i++) {
      if (Math.abs(r - SIZES[i]) <= 0.45) return SIZES[i];
    }
    return 0;
  }

  // jsPDF's splitTextToSize breaks lines only at spaces, so a line break stands
  // for one space. The exception is a space-free token wider than the wrap
  // width: jsPDF chops it with no hyphen. That shows up as a line filled to
  // within a couple of glyphs whose last token, together with the next line's
  // first token, is wider than a whole line, which no two real words are.
  function isChop(a, b, wrap) {
    if (!a || a.page !== b.page) return false;
    var wa = a.xEnd - a.x;
    var wb = b.xEnd - b.x;
    if (!(wa > 0) || !(wb > 0)) return false;
    var perA = wa / a.text.length;
    var perB = wb / b.text.length;
    if (wa < wrap - perA * 2.5) return false;
    var ta = /\S+$/.exec(a.text);
    var tb = /^\S+/.exec(b.text);
    return !!(ta && tb) && ta[0].length * perA + tb[0].length * perB > wrap;
  }

  function joinLines(list, wrap) {
    var out = "";
    for (var i = 0; i < list.length; i++) {
      if (i === 0) out = list[i].text;
      else out += (isChop(list[i - 1], list[i], wrap) ? "" : " ") + list[i].text;
    }
    return out;
  }

  function unquote(s) {
    if (s.length >= 2 && s.charAt(0) === "\"" && s.charAt(s.length - 1) === "\"") s = s.slice(1, -1);
    return s.trim();
  }

  function failed(reason) {
    return { ok: false, reason: reason, variant: "", blueprint: {}, references: [], extra: [] };
  }

  function fromTextContent(pages) {
    if (pages && !isArray(pages) && pages.items) pages = [pages];
    if (!isArray(pages) || !pages.length) return failed("This PDF has no readable text.");
    var lines = buildLines(pages);
    if (!lines.length) return failed("This PDF has no readable text.");
    var i;

    // Header, page 1: "Character Commission Blueprint" (10pt) fixes the scale,
    // then the name is the largest text after it.
    var sub = -1;
    for (i = 0; i < lines.length && i < 6 && lines[i].page === 0; i++) {
      if (norm(lines[i].text) === SUBTITLE) {
        sub = i;
        break;
      }
    }
    if (sub < 0) return failed("This PDF is not a FramedFantasy Character Commission Blueprint.");
    var scale = lines[sub].size / 10 || 1;
    var nameAt = -1;
    for (i = sub + 1; i < lines.length && lines[i].page === 0; i++) {
      var rel = lines[i].size / scale;
      if (rel >= 25 && rel <= 45 && (nameAt < 0 || lines[i].size > lines[nameAt].size)) nameAt = i;
    }
    if (nameAt < 0) return failed("This blueprint has no character name.");

    function cls(line) {
      return sizeClass(line.size, scale);
    }

    var pos = nameAt + 1;
    var raceLine = pos < lines.length && cls(lines[pos]) === 13 ? lines[pos++] : null;
    var archLine = pos < lines.length && cls(lines[pos]) === 12 && norm(lines[pos].text).indexOf(FOOTER) !== 0 ? lines[pos++] : null;

    // Body: section titles 10pt, labels 8pt upper case, values 11pt; then the
    // optional references (8pt captions, 10pt quoted notes); then the footer.
    var fields = {};
    var references = [];
    var extra = [];
    var target = null; // {key, label} for a label, {key:null, label:""} for stray text, {ref} for a note
    var buf = [];
    var inReferences = false;
    var lastRef = null;
    var notedRef = null;
    var sawStory = false;
    var footerAt = -1;

    function flush() {
      if (target) {
        var text = joinLines(buf, (target.ref ? NOTE_WRAP : VALUE_WRAP) * scale);
        if (target.ref) {
          target.ref.note = unquote(text);
        } else if (text) {
          if (target.key && !has(fields, target.key)) fields[target.key] = text;
          else extra.push({ label: target.label, value: text });
        }
      }
      target = null;
      buf = [];
    }

    for (i = pos; i < lines.length; i++) {
      var line = lines[i];
      var c = cls(line);
      var n = norm(line.text);
      if (c === 12 && n.indexOf(FOOTER) === 0) {
        footerAt = i;
        break;
      }
      if (!inReferences) {
        if (c === 10 && has(SECTION, n)) {
          flush();
          if (n === "STORY") sawStory = true;
          continue;
        }
        if (c === 10 && n === REFERENCES) {
          flush();
          inReferences = true;
          continue;
        }
        if (c === 8 && /[A-Z]/.test(line.text) && line.text === line.text.toUpperCase()) {
          flush();
          target = { key: has(LABEL_KEY, n) ? LABEL_KEY[n] : null, label: line.text };
          continue;
        }
        if (c === 11) {
          if (!target) target = { key: null, label: "" };
          buf.push(line);
          continue;
        }
      } else {
        var cap = c === 8 ? CAPTION_RE.exec(line.text) : null;
        if (cap) {
          flush();
          lastRef = { index: parseInt(cap[1], 10), note: "" };
          references.push(lastRef);
          continue;
        }
        if (c === 10 && target && target.ref) {
          buf.push(line);
          continue;
        }
        if (c === 10 && !target && lastRef && notedRef !== lastRef) {
          target = { ref: lastRef };
          notedRef = lastRef;
          buf.push(line);
          continue;
        }
      }
      // Nothing the builders draw at this point: keep the text rather than drop it.
      if (!target || target.ref || target.key !== null || target.label !== "") {
        flush();
        target = { key: null, label: "" };
      }
      buf.push(line);
    }
    flush();

    var footerName = null;
    var url = "";
    if (footerAt >= 0) {
      var footerLines = [];
      for (i = footerAt; i < lines.length && cls(lines[i]) === 12; i++) footerLines.push(lines[i]);
      var fm = FOOTER_RE.exec(joinLines(footerLines, NOTE_WRAP * scale));
      if (fm) footerName = fm[1];
      for (; i < lines.length; i++) {
        if (cls(lines[i]) === 14) url = norm(lines[i].text);
      }
    }

    var out = {};
    // Header lines are centred and never wrapped. When one is wider than the page
    // it starts left of x=0 and pdf.js drops the glyphs beyond both edges, so its
    // ends are unreliable (e.g. "Level 17" reads "Level 1").
    var name = lines[nameAt].text;
    if (footerName !== null) {
      if (lines[nameAt].x < 0 && footerName.indexOf(name) >= 0) name = footerName; // the footer repeats it in full
      else if (name === "Unnamed Hero" && footerName === "your hero") name = ""; // the builder's placeholder
    }
    if (name) out.name = name;

    var level = "";
    if (raceLine) {
      var rm = RACE_RE.exec(raceLine.text);
      if (raceLine.x >= 0 && rm) {
        if (rm[1] && rm[1] !== "Unknown") out.race = rm[1];
        if (rm[2] && rm[2] !== "Unknown") out.charClass = rm[2];
        level = rm[3] || "";
      } else if (raceLine.x < 0) {
        var parts = raceLine.text.split(" . ");
        if (parts.length === 3 && parts[1] !== "Unknown") out.charClass = parts[1];
      }
    }
    if (/^\d+$/.test(level)) out.level = parseInt(level, 10);
    else if (level && level !== "undefined" && level !== "null") out.level = level;

    var verdict = false;
    if (archLine && archLine.x >= 0) {
      // Quest 2 prints verdictTitle + "  \u00b7  Born from Fire" in this slot.
      var title = archLine.text.replace(/\s*\u00b7\s*Born from Fire$/, function () {
        verdict = true;
        return "";
      });
      if (title) out.archetypeTitle = title;
    }

    for (i = 0; i < FIELDS.length; i++) {
      if (has(fields, FIELDS[i][0])) out[FIELDS[i][0]] = fields[FIELDS[i][0]];
    }

    var variant = "quest1";
    if (url === "FRAMEDFANTASYCOM" || (url !== "FRAMEDFANTASYCOMDND" && (sawStory || verdict))) variant = "quest2";

    return { ok: true, reason: "", variant: variant, blueprint: out, references: references, extra: extra };
  }

  // ---- reading a file with pdf.js ----

  var loading = null;
  var worker = null;

  function plain(message) {
    var e = new Error(message);
    e.ffPlain = true;
    return e;
  }

  function prepare(lib) {
    if (!lib.GlobalWorkerOptions.workerSrc) lib.GlobalWorkerOptions.workerSrc = WORKER_URL;
    return lib;
  }

  function loadPdfJs() {
    if (window.pdfjsLib && window.pdfjsLib.getDocument) return Promise.resolve(prepare(window.pdfjsLib));
    if (loading) return loading;
    loading = new Promise(function (resolve, reject) {
      var s = document.createElement("script");
      var timer = setTimeout(function () {
        done(plain("Loading the PDF reader timed out. Check the connection and try again."));
      }, 15000);
      function done(err) {
        clearTimeout(timer);
        s.onload = s.onerror = null;
        if (!err && !(window.pdfjsLib && window.pdfjsLib.getDocument)) err = plain("Could not load the PDF reader.");
        if (err) {
          loading = null;
          reject(err);
        } else {
          resolve(prepare(window.pdfjsLib));
        }
      }
      s.onload = function () { done(null); };
      s.onerror = function () { done(plain("Could not load the PDF reader. Check the connection and try again.")); };
      s.async = true;
      s.src = PDFJS_URL;
      (document.head || document.documentElement).appendChild(s);
    });
    return loading;
  }

  function readBytes(file) {
    return new Promise(function (resolve, reject) {
      // pdf.js takes ownership of the bytes it is given, so always pass a copy.
      if (typeof ArrayBuffer !== "undefined" && file instanceof ArrayBuffer) return resolve(new Uint8Array(file.slice(0)));
      if (typeof Uint8Array !== "undefined" && file instanceof Uint8Array) return resolve(new Uint8Array(file));
      if (!file || typeof Blob === "undefined" || !(file instanceof Blob)) return reject(plain("Choose a PDF file first."));
      var fr = new FileReader();
      fr.onload = function () { resolve(new Uint8Array(fr.result)); };
      fr.onerror = function () { reject(plain("Could not read the file.")); };
      fr.readAsArrayBuffer(file);
    });
  }

  function textOf(pdf) {
    var pages = [];
    function next(n) {
      if (n > pdf.numPages) return pages;
      return pdf.getPage(n).then(function (page) {
        return page.getTextContent({ disableNormalization: true }); // keeps e.g. "\u00b5" as typed
      }).then(function (tc) {
        pages.push(tc);
        // The header is on page 1, so an unrelated PDF stops here.
        if (n === 1 && !fromTextContentHeaderSeen(tc)) return pages;
        return next(n + 1);
      });
    }
    return Promise.resolve(next(1));
  }

  function fromTextContentHeaderSeen(tc) {
    var lines = buildLines([tc]);
    for (var i = 0; i < lines.length && i < 6; i++) {
      if (norm(lines[i].text) === SUBTITLE) return true;
    }
    return false;
  }

  function friendly(e) {
    if (e && e.ffPlain) return e;
    if (e && e.name === "InvalidPDFException") return plain("This file is not a readable PDF.");
    if (e && e.name === "PasswordException") return plain("This PDF is password protected.");
    return plain("Could not read this PDF.");
  }

  function readPdf(file) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var task = null;
      var timer = setTimeout(function () {
        finish(plain("Reading the PDF took too long."));
      }, 45000);
      function finish(err, value) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (task) {
          try {
            var gone = task.destroy();
            if (gone && gone.then) gone.then(null, function () {});
          } catch (e) { /* the document is being discarded anyway */ }
        }
        if (err) reject(friendly(err));
        else resolve(value);
      }
      readBytes(file).then(function (bytes) {
        return loadPdfJs().then(function (lib) {
          if (settled) return null;
          var params = { data: bytes, isEvalSupported: false, enableXfa: false };
          // One worker for every read, so later reads skip the worker start-up.
          if (lib.PDFWorker) {
            if (!worker || worker.destroyed) worker = new lib.PDFWorker({ name: "ff-blueprint" });
            params.worker = worker;
          }
          task = lib.getDocument(params);
          return task.promise;
        });
      }).then(function (pdf) {
        return pdf ? textOf(pdf) : null;
      }).then(function (pages) {
        if (pages) finish(null, fromTextContent(pages));
      }).then(null, function (e) {
        // Last in the chain so a throw from the parse above is caught too.
        finish(e);
      });
    });
  }

  window.ffBlueprintFromTextContent = fromTextContent;
  window.ffReadBlueprintPdf = readPdf;
})();
